import _ from 'lodash';
import {
  ELEMENT_IMAGE,
  ELEMENT_LINK,
  ELEMENT_OL,
  ELEMENT_UL,
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
  ELEMENT_TR,
  ELEMENT_TD,
  ELEMENT_TH,
} from '@udecode/plate';
import { jsx } from 'slate-hyperscript';
import { KEY_ALIGN } from '@udecode/plate-alignment';
import { MARK_COLOR, MARK_FONT_SIZE } from '@udecode/plate-font';
import { Text } from 'slate';

import { translateFontTagSize } from 'components/text_editor_new/lib/translate_font_size';
import { removeExtraBreaks } from 'components/text_editor_new/lib/remove_extra_breaks';

import { hasPluginOfType } from 'components/text_editor_new/lib/has_plugin_of_type';
import { hasPluginWithKey } from 'components/text_editor_new/lib/has_plugin_with_key';
import sanitize from 'components/text_editor_new/lib/sanitize_html';
import { shouldAllowElementColor } from 'components/text_editor_new/lib/should_allow_element_color';
import { MAX_TABLE_COLUMNS } from 'components/text_editor_new/lib/table_constants';

/**
 * Beware - the `editor` parameter is optional and in many cases will be null/undefined. Also, the editor
 * instance may not match the "main" editor in the current composer
 */
export default function deserializeHtml(html, editor) {
  const sanitizedHtml = sanitize(html);
  const transformedHtml = removeExtraBreaks(sanitizedHtml);
  const parsedDocument = new DOMParser().parseFromString(transformedHtml, 'text/html');

  // Get the fragment, then go over the top nodes and make sure there are no unwrapped text nodes at the top.
  // Slate prohibits inline nodes at the top level and removes all unwrapped texts, so we have to wrap loose
  // text nodes into paragraphs.
  // See https://docs.slatejs.org/concepts/11-normalizing#built-in-constraints
  const fragment = deserializeHtmlElement(parsedDocument.body, {}, editor);
  const collatedNodes = collateNodes(fragment);

  return _.map(collatedNodes, nodeGroup => {
    if (isInlineNode(nodeGroup[0])) {
      return jsx('element', { type: ELEMENT_PARAGRAPH }, nodeGroup);
    }
    return nodeGroup[0];
  });
}

export function deserializeHtmlElement(el, markAttributes = {}, editor) {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const nodeAttributes = buildNodeStyleAttributes(el, markAttributes, editor);
  const children = Array.from(el.childNodes)
    .map(node => deserializeHtmlElement(node, nodeAttributes, editor))
    .flat();

  if (!children.length) {
    children.push(jsx('text', nodeAttributes, ''));
  }

  // If we have a list plugin available, we'll actually deserialize lists as expected. Without a list plugin,
  // we'll fall through to just treating LI items as paragraphs below.
  if (hasPluginWithKey(editor, 'list')) {
    switch (el.nodeName) {
      case 'OL': {
        return jsx('element', { type: ELEMENT_OL }, children);
      }
      case 'UL': {
        return jsx('element', { type: ELEMENT_UL }, children);
      }
      case 'LI': {
        const align = el.style.getPropertyValue('text-align');
        const node = jsx('element', { type: ELEMENT_LI }, wrapLIChildren(children));
        if (align && align !== 'left') {
          node[KEY_ALIGN] = align;
        }
        return node;
      }

      default:
        break;
    }
  }

  // Tables (e.g. pasted from Google Sheets / Excel). Guarded on the table plugin so that without it
  // we fall through and treat cells as plain paragraphs. THEAD/TBODY/TFOOT are intentionally not
  // handled here: they hit the default case below (returning their children), which the parent
  // TABLE flattens back into a flat list of rows.
  if (hasPluginWithKey(editor, 'table')) {
    switch (el.nodeName) {
      case 'TABLE': {
        const rows = children.filter(node => node && node.type === ELEMENT_TR);
        if (rows.length) {
          return jsx('element', { type: ELEMENT_TABLE }, expandTableGrid(rows));
        }
        break;
      }
      case 'TR': {
        const cells = children.filter(node => node && (node.type === ELEMENT_TD || node.type === ELEMENT_TH));
        if (cells.length) {
          return jsx('element', { type: ELEMENT_TR }, cells);
        }
        break;
      }
      case 'TD':
      case 'TH': {
        const type = el.nodeName === 'TH' ? ELEMENT_TH : ELEMENT_TD;
        const attrs = {};
        // colSpan is kept (Plate 21 supports it); rowSpan is only kept transiently — expandTableGrid (at
        // the TABLE case) turns vertical merges into placeholder cells at the right column offset and then
        // strips rowSpan, since Plate can't represent it. colSpan is bounded so a crafted `colspan` value
        // can't blow up the grid we build.
        const colSpan = parseInt(el.getAttribute('colspan'), 10);
        const rowSpan = parseInt(el.getAttribute('rowspan'), 10);
        if (colSpan > 1) attrs.colSpan = Math.min(colSpan, MAX_TABLE_COLUMNS);
        if (rowSpan > 1) attrs.rowSpan = rowSpan;
        // Preserve cell background (e.g. shaded header rows from a spreadsheet). Unlike text color,
        // we don't luminosity-filter it — a black cell is intentional and Supernova already allows
        // background-color on td/th.
        const background = el.style && el.style.backgroundColor;
        if (background) attrs.background = background;
        // Text alignment (e.g. right-aligned numbers) rides on the <td> in spreadsheet pastes.
        // Alignment is a paragraph-level prop in our model, so push it onto the cell's paragraphs.
        const cellAlign = el.style.getPropertyValue('text-align');
        const align = cellAlign && cellAlign !== 'left' && cellAlign !== 'start' ? cellAlign : '';
        return jsx('element', { type, ...attrs }, wrapCellChildren(children, align));
      }

      default:
        break;
    }
  }

  switch (el.nodeName) {
    case 'BODY':
      return jsx('fragment', {}, children);

    case 'DIV':
    case 'LI':
    case 'P': {
      const align = el.style.getPropertyValue('text-align');
      // If a paragraph-like element only contains a <br>, that means it's empty. Instead of
      // recursively deserializing the single <br> tag (which will resolve to a newline, creating
      // a unwanted extra line), we'll just short-circuit and return an empty text node.
      const isEmpty = el.childNodes.length === 1 && el.childNodes[0].nodeName === 'BR';
      const resolvedChildren = isEmpty ? [jsx('text', nodeAttributes, '')] : children;
      const node = jsx('element', { type: ELEMENT_PARAGRAPH }, resolvedChildren);
      if (align && align !== 'left' && align !== 'start') {
        node[KEY_ALIGN] = align;
      }

      return node;
    }

    case 'IMG':
      if (hasPluginOfType(editor, ELEMENT_IMAGE)) {
        const src = el.getAttribute('src');
        const attachmentId = el.getAttribute('data-attachment-id');
        const fileName = el.getAttribute('data-file');
        const decodedSrc = src && decodeURI(src);

        return jsx('element', { attachmentId, fileName, src: decodedSrc, type: ELEMENT_IMAGE }, children);
      }
      break;

    case 'PLACEHOLDER':
      return jsx('element', { type: 'placeholder' }, children);

    case 'VARIABLE': {
      const attributes = {
        'data-id': el.getAttribute('data-id'),
        'data-type': el.getAttribute('data-type'),
        'data-value': el.getAttribute('data-value'),
      };
      return jsx('element', { attributes, type: 'variable' }, children);
    }

    case 'AGENTMENTION': {
      const attributes = { 'data-agentid': el.getAttribute('data-agentid') };
      return jsx('element', { attributes, type: 'agent-mention' }, children);
    }

    case 'A': {
      if (hasPluginOfType(editor, ELEMENT_LINK)) {
        const targetAttr = el.getAttribute('target') || '';
        const sanitizedTarget = ['_blank', '_self'].includes(targetAttr) ? targetAttr : '';
        const nodeTargetAttr = sanitizedTarget ? { target: sanitizedTarget } : {};

        return jsx('element', { url: el.getAttribute('href'), type: ELEMENT_LINK, ...nodeTargetAttr }, children);
      }
      return children;
    }

    case 'BR': {
      return jsx('text', markAttributes, '\n');
    }

    default:
      return children;
  }
}

/**
 * Builds a map of node attributes that control the element appearance (font style, color etc.)
 *
 * @param {HTMLElement} element
 * @param {object} initialAttributes
 * @param {object} [editor] - optional Slate editor; when provided, marks that require a plugin
 *                            (e.g. MARK_COLOR, MARK_FONT_SIZE) are only attached when the
 *                            corresponding plugin is loaded on the editor.
 * @return {object}
 */
function buildNodeStyleAttributes(element, initialAttributes = {}, editor) {
  const nodeAttributes = { ...initialAttributes };
  if (!element) return nodeAttributes;

  // Formatting can ride on inline CSS on ANY element — Google Sheets / Docs put color, size, and
  // weight directly on <td>/<div>, not just on a <span>. Read it generically (rather than only for
  // <span>) so pasted formatting — bold headers, colored cell text — survives whatever tag carries it.
  const style = element.style;
  if (style) {
    if (isBoldFontWeight(style.fontWeight)) {
      nodeAttributes.bold = true;
    }
    if (hasPluginWithKey(editor, MARK_COLOR) && style.color && shouldAllowElementColor(style.color)) {
      nodeAttributes[MARK_COLOR] = style.color;
    }
    // Skip `medium` — it's the CSS default, and browsers stamp it onto the inline styles of copied
    // content (e.g. every cell of a pasted spreadsheet table), which would otherwise litter the
    // output with redundant font-size marks.
    if (hasPluginWithKey(editor, MARK_FONT_SIZE) && style.fontSize && style.fontSize !== 'medium') {
      nodeAttributes[MARK_FONT_SIZE] = style.fontSize;
    }
  }

  switch (element.nodeName) {
    case 'STRONG':
    case 'B':
      nodeAttributes.bold = true;
      break;

    case 'EM':
    case 'I':
      nodeAttributes.italic = true;
      break;

    case 'U':
      nodeAttributes.underline = true;
      break;

    // Legacy font color and size. We still support it in order to handle existing Answers and items.
    // Marks are only applied when the editor has the corresponding plugin loaded — otherwise the
    // composer would silently carry an unrendered mark that the serializer re-emits on send.
    case 'FONT': {
      const colorAttribute = _.trim(element.getAttribute('color'));
      const sizeAttribute = _.trim(element.getAttribute('size'));
      const translatedSize = (sizeAttribute && translateFontTagSize(sizeAttribute)) || '';

      if (hasPluginWithKey(editor, MARK_COLOR) && colorAttribute && shouldAllowElementColor(colorAttribute)) {
        nodeAttributes[MARK_COLOR] = colorAttribute;
      }
      if (hasPluginWithKey(editor, MARK_FONT_SIZE) && translatedSize) {
        nodeAttributes[MARK_FONT_SIZE] = translatedSize;
      }
      break;
    }

    // Current font color and size formatting on <span> is handled by the generic inline-CSS reader
    // at the top of this function (it also covers <td>/<div> etc.).

    default:
      break;
  }

  return nodeAttributes;
}

/**
 * Walks through the list of nodes and groups consequent "inline" nodes together. Returns and array of
 * "node groups". Non-inline nodes will always be in a single-element group (by themselves)
 *
 * @param {object[]} nodes
 * @returns {object[][]}
 */
function collateNodes(nodes) {
  return _.reduce(
    nodes,
    (acc, node) => {
      if (!acc.length) return [[node]];
      if (!isInlineNode(node)) {
        acc.push([node]);
        return acc;
      }

      // If we are here, we are on an "inline" node. Now check the last group of nodes that we visited
      // and see if this node should join the group or start a new one
      const lastGroup = acc.pop();
      if (isInlineNode(lastGroup[0])) {
        lastGroup.push(node);
        acc.push(lastGroup);
      } else {
        acc.push(lastGroup);
        acc.push([node]);
      }

      return acc;
    },
    []
  );
}

/**
 * Helper function that checks whether the given node should be treated as "inline" for the purpose
 * of grouping. NOTE: this helper DOES NOT cover all inline nodes (i.e. it ignores all list-related
 * nodes) because it is NOT a general purpose utility. Its purpose in life is to help with collating
 * all nodes that should go into the same paragraph or <LIC> element
 *
 * @param node
 * @returns {boolean}
 */
function isInlineNode(node) {
  const inlineNodeTypes = ['A', 'AGENT-MENTION', 'PLACEHOLDER', 'VARIABLE'];
  const nodeType = (node?.type || '').toUpperCase();

  return node && (Text.isText(node) || inlineNodeTypes.includes(nodeType));
}

function wrapLIChildren(children) {
  // Before we can wrap LI children, we need to collate text nodes so that each
  // line does not become its own "element", which will break up list items with
  // colored text. We group consequent text nodes together so that they become
  // children of the same LIC element
  const collated = collateNodes(children);

  return _.map(collated, nodeGroup => {
    // Wrap raw text nodes in LIC element as expected by the Plate list plugin. Wrap only text nodes
    const firstChild = nodeGroup[0];

    // Non-inline children will always be in a "group" by themselves (a group of one node)
    return isInlineNode(firstChild) ? jsx('element', { type: ELEMENT_LIC }, nodeGroup) : firstChild;
  });
}

// True when a CSS font-weight represents bold. Covers the keyword forms and numeric weights
// (Google Sheets/Docs emit `font-weight:700`), so pasted bold text maps to our bold mark.
function isBoldFontWeight(fontWeight) {
  if (!fontWeight) return false;
  const normalized = String(fontWeight).toLowerCase();
  if (normalized === 'bold' || normalized === 'bolder') return true;
  return parseInt(normalized, 10) >= 700;
}

// Expands vertical merges into a rectangular-safe grid. Plate 21 can't represent rowSpan, so a merged
// cell would leave the rows it spans short a cell — and because rows are built independently, a merge
// that isn't in the last column would shift every following cell left. We walk the grid tracking which
// columns each rowSpan still occupies, and for those columns in later rows we insert an empty placeholder
// cell at the correct offset (then drop the rowSpan attribute). colSpan is kept and counted when advancing
// the column cursor. Everything is bounded by MAX_TABLE_COLUMNS so a crafted paste can't blow up.
function expandTableGrid(rows) {
  const carry = []; // carry[col] = number of upcoming rows still occupied by a rowSpan at that column

  return _.map(rows, row => {
    const cells = _.filter(row.children, Boolean);
    const newCells = [];
    let col = 0;

    // Emit placeholders for any columns a previous row's rowSpan still occupies at the current cursor.
    const fillCarriedColumns = () => {
      while (col < MAX_TABLE_COLUMNS && carry[col] > 0) {
        newCells.push(placeholderCell());
        carry[col] -= 1;
        col += 1;
      }
    };

    for (const cell of cells) {
      fillCarriedColumns();
      if (col >= MAX_TABLE_COLUMNS) break;

      const colSpan = Math.min(cell.colSpan || 1, MAX_TABLE_COLUMNS - col);
      const rowSpan = Math.min(cell.rowSpan || 1, rows.length);
      newCells.push(_.omit(cell, 'rowSpan'));

      // Claim every column this cell covers: set a downward carry when it spans rows, otherwise clear the
      // slot. Assigning across the whole span (not just when rowSpan > 1) consumes any stale carry from an
      // earlier row that this cell overlaps — without this, a colSpan cell would step past an occupied
      // column and leave a dangling carry that misplaces a placeholder in a later row.
      for (let c = col; c < col + colSpan; c += 1) {
        carry[c] = rowSpan > 1 ? rowSpan - 1 : 0;
      }
      col += colSpan;
    }

    fillCarriedColumns();

    return { ...row, children: newCells };
  });
}

function placeholderCell() {
  return jsx('element', { type: ELEMENT_TD }, [jsx('element', { type: ELEMENT_PARAGRAPH }, [{ text: '' }])]);
}

// Table cells require block children (paragraphs), so we collate runs of inline/text nodes and wrap
// each run in a paragraph. Block children (e.g. an already-deserialized <div>) pass through as-is.
// `align` (from the cell's text-align) is applied to the paragraphs we create here.
function wrapCellChildren(children, align) {
  // Sources like Numbers wrap each cell's content in a <p> and pretty-print with newlines around it.
  // Those whitespace-only text nodes would otherwise each become an empty paragraph (extra blank
  // lines), so when the cell already has block children we drop the insignificant whitespace.
  const hasBlockChild = children.some(node => node && !isInlineNode(node));
  const relevantChildren = hasBlockChild
    ? children.filter(node => !(Text.isText(node) && node.text.trim() === ''))
    : children;
  const collated = collateNodes(relevantChildren);

  return _.map(collated, nodeGroup => {
    const firstChild = nodeGroup[0];
    if (!isInlineNode(firstChild)) {
      return firstChild;
    }
    const paragraph = jsx('element', { type: ELEMENT_PARAGRAPH }, nodeGroup);
    if (align) {
      paragraph[KEY_ALIGN] = align;
    }
    return paragraph;
  });
}
